import * as Sentry from '@sentry/browser';
import invoke from 'lodash.invoke';
import namespace from '../../../upi/namespace';
import * as PackageInfo from '../../../upi/packageInfo';
import {getLocale} from '@util/LocaleUtils';
import {hasLocalStorage} from '@util/StorageUtils';
import {check, validator} from '@util/ObjectUtils';

// note -> only send logs for production environment
const HOST = window.location.host;

const LOGGING_ENABLED =
    process.env.LOGGING_ENABLED ||
    (process.env.NODE_ENV === 'production' && /(stage|www)\.ultradent\.com/.test( HOST ));

const LOGGING_ENV =
    /(www)\.ultradent\.com/.test( HOST ) ? 'Production Frontend'
        : /(stage)\.ultradent\.com/.test( HOST ) ? 'Staging Frontend'
        : process.env.LOGGING_ENV;

const hasMessageField = check( validator(
    'argument must contain a "message" property for logging',
    obj => !!obj && obj.message != null && obj.message !== ''
) );

class LoggingProvider {
    constructor () {
        const provider = namespace( '__LOGGING_PROVIDER__' );

        if ( provider.instance ) {
            return provider.instance;
        }

        LoggingProvider.invoke( 'init', {
            dsn: 'https://b4809697c8414e498953f7bce4fb133a@sentry.io/252874'
        } );

        this.configure( {
            tags: {
                environment: LOGGING_ENV,
                upi_frontend: PackageInfo.version,
                page_locale: getLocale(),
                enabled_cookies: window.navigator.cookieEnabled,
                enabled_localstorage: hasLocalStorage()
            }
        } );

        return provider.instance = this;
    }

    static invoke ( cmd, ...rest ) {
        const options = rest != null ? rest : {};
        if ( LOGGING_ENABLED ) {
            invoke( Sentry, cmd, ...options );
        }
    }

    /**
     * "command" param in the following format
     * "commandName": [SPREAD_ARRAY_PARAMS]
     *
     * usage:
     * configure({
     *     level: 'debug',
     *     extra: {batter: 0.5},
     *     user: {email: "troy.watt@ultradent.com"},
     *     tag: {one: 'tag only'},
     *     tags: {
     *           version: 123,
     *          'page_local':'en-us'
     *     }
     * })
     *
     *
     *
     */
    configure ( commands ) {
        // console.log( 'logger [configure]' );
        LoggingProvider.invoke( 'configureScope', scope => {
            Object.entries( commands ).forEach(
                ( [prop, args] ) => {
                    if ( prop === 'tags' ) {
                        return this.setTagsForScope( scope, args );
                    }

                    const params = Array.isArray( args ) ? args : [args];
                    const cmd = 'set' + prop.substr( 0, 1 ).toUpperCase() + prop.substr( 1 );
                    return invoke( scope, cmd, ...params );
                }
            )
        } );
    }

    setTagsForScope ( scope, tags = {} ) {
        // map tags object to individual calls of 'setTag' on scope to conform to Sentry api
        Object.entries( tags )
            .forEach( tag => invoke( scope, 'setTag', ...tag ) );
    }

    capture ( err ) {
        // console.log( 'logger [captureException]' );
        LoggingProvider.invoke( 'captureException', err );
    }

    addBreadcrumb ( obj ) {
        const errs = hasMessageField( obj );
        if ( errs.length ) {
            throw TypeError( `LoggingProvider.addBreadcrumb ${errs[0]}` );
        }
        LoggingProvider.invoke( 'addBreadcrumb', obj );
    }

    triggerEvent ( obj ) {
        const errs = hasMessageField( obj );
        if ( errs.length ) {
            throw TypeError( `LoggingProvider.triggerEvent ${errs[0]}` );
        }
        LoggingProvider.invoke( 'captureEvent', obj );
    }

    sendMessage ( str ) {
        if ( typeof str !== 'string' ) {
            throw new TypeError( `LoggingProvider.sendMessage expects a string for the first argument but received ${str} instead` );
        }
        LoggingProvider.invoke( 'captureMessage', str );
    }
}

const logger = new LoggingProvider;
export default logger;